import { v4 as uuidv4 } from "uuid"
import firebase from "@/firebase"
import type { AcInventoryItem, AcInventoryBatch, AcInventoryUpdate, AcInventoryWorksite } from "@/types/inventory"
import type { fetchedUser } from "@/types/fetchedUser"

// Shown wherever a removal has no destination worksite recorded.
export const UNSPECIFIED_WORKSITE_LABEL = "Şantiye Belirtilmemiş"

// Display name for a (possibly missing) worksite destination.
export const getWorksiteLabel = (worksite?: AcInventoryWorksite | null): string => {
    return worksite?.name || UNSPECIFIED_WORKSITE_LABEL
}

// Turkish labels for the quantity types used across the inventory module.
export const quantityTypeLabels: Record<NonNullable<AcInventoryItem["quantityType"]>, string> = {
    PIECE: "adet",
    METER: "metre",
    LITER: "litre",
}

export const quantityTypeOptions: { value: AcInventoryItem["quantityType"], label: string }[] = [
    { value: "PIECE", label: "Adet" },
    { value: "METER", label: "Metre" },
    { value: "LITER", label: "Litre" },
]

export const getQuantityLabel = (quantityType: AcInventoryItem["quantityType"]): string => {
    return quantityType ? quantityTypeLabels[quantityType] : ""
}

// ---- Currency ----
export const currencyOptions: { value: NonNullable<AcInventoryItem["purchaseCurrency"]>, label: string }[] = [
    { value: "TRY", label: "₺ TRY" },
    { value: "USD", label: "$ USD" },
    { value: "EUR", label: "€ EUR" },
]

export const currencySymbols: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€" }

// ---- Condition ----
export const conditionLabels: Record<NonNullable<AcInventoryBatch["condition"]>, string> = {
    NEW: "Yeni",
    USED: "Kullanılmış",
    REFURBISHED: "Yenilenmiş",
}

export const conditionOptions: { value: AcInventoryBatch["condition"], label: string }[] = [
    { value: "NEW", label: "Yeni" },
    { value: "USED", label: "Kullanılmış" },
    { value: "REFURBISHED", label: "Yenilenmiş" },
]

// ---- Batches ----

// Build a new purchase batch with a stable id and audit fields.
export const createBatch = (params: {
    quantity: number,
    unitPrice: number,
    purchaseCurrency: NonNullable<AcInventoryBatch["purchaseCurrency"]>,
    condition: NonNullable<AcInventoryBatch["condition"]>,
    purchaseDate?: Date | string,
    note?: string,
    addedBy?: fetchedUser,
}): AcInventoryBatch => {
    const batch: AcInventoryBatch = {
        id: uuidv4(),
        quantity: params.quantity,
        initialQuantity: params.quantity,
        unitPrice: params.unitPrice,
        purchaseCurrency: params.purchaseCurrency,
        // Default to a date-only (midnight) value matching how the batch form stores
        // purchaseDate (`new Date("YYYY-MM-DD")`), so a seed batch and a same-day form
        // batch compare equal and FIFO falls back to `addedAt` for true insertion order.
        purchaseDate: params.purchaseDate ?? new Date(new Date().toISOString().slice(0, 10)),
        condition: params.condition,
        addedAt: new Date(),
    }
    if (params.note) batch.note = params.note
    if (params.addedBy) batch.addedBy = params.addedBy
    return batch
}

// Total remaining quantity across batches.
export const sumBatchQuantity = (batches?: AcInventoryBatch[]): number => {
    if (!batches || !batches.length) return 0
    return batches.reduce((total, batch) => total + (batch.quantity || 0), 0)
}

const toMillis = (value: Date | string | undefined): number => {
    if (!value) return 0
    // Firestore Timestamp support
    const anyValue = value as any
    if (anyValue?.toDate) return anyValue.toDate().getTime()
    return new Date(value).getTime()
}

// Sort batches oldest-first by purchase date (FIFO order). Purchase dates are often
// stored date-only (midnight), so same-day batches would otherwise tie; `addedAt` (a
// full insertion timestamp) breaks the tie so true insertion order is preserved.
export const sortBatchesFIFO = (batches: AcInventoryBatch[]): AcInventoryBatch[] => {
    return [...batches].sort((a, b) => {
        const byPurchase = toMillis(a.purchaseDate) - toMillis(b.purchaseDate)
        if (byPurchase !== 0) return byPurchase
        return toMillis(a.addedAt) - toMillis(b.addedAt)
    })
}

// Consume `amount` from batches, oldest first. Returns a NEW batches array with
// fresh batch objects — the input batches are never mutated, so callers can safely
// discard the result if a write fails. Depleted batches are kept (quantity 0) so
// they remain in the purchase history.
export const consumeBatchesFIFO = (
    batches: AcInventoryBatch[],
    amount: number
): AcInventoryBatch[] => {
    const ordered = sortBatchesFIFO(batches).map((batch) => ({ ...batch }))
    let remaining = amount
    for (const batch of ordered) {
        if (remaining <= 0) break
        const take = Math.min(batch.quantity, remaining)
        batch.quantity -= take
        remaining -= take
    }
    return ordered
}

// Sum of remaining purchase cost grouped by currency (handles mixed-currency items).
export const purchaseCostByCurrency = (batches?: AcInventoryBatch[]): { currency: string, total: number }[] => {
    if (!batches || !batches.length) return []
    const totals: Record<string, number> = {}
    batches.forEach((batch) => {
        const cost = (batch.quantity || 0) * (batch.unitPrice || 0)
        totals[batch.purchaseCurrency] = (totals[batch.purchaseCurrency] || 0) + cost
    })
    return Object.entries(totals).map(([currency, total]) => ({ currency, total }))
}

export const formatMoney = (value: number | null | undefined, currency?: string): string => {
    if (value === undefined || value === null) return "-"
    const symbol = currency ? (currencySymbols[currency] || currency) : ""
    return `${value.toLocaleString("tr-TR")} ${symbol}`.trim()
}

// ---- Quantity validation ----
// PIECE stock must be whole units; METER/LITER may be fractional.
export const requiresWholeUnits = (quantityType: AcInventoryItem["quantityType"]): boolean =>
    quantityType === "PIECE"

// ---- Atomic stock movements ----
// Both helpers re-read the item INSIDE a Firestore transaction and recompute the
// batches/quantity from fresh data, so concurrent add/remove operations can't clobber
// one another (lost-update race). They return the persisted state for local UI sync.

const INVENTORY_ITEMS_COLLECTION = "inventory/items/list"

export interface StockMovementResult {
    batches: AcInventoryBatch[],
    quantity: number,
    update: AcInventoryUpdate,
}

// Add a new purchase batch atomically.
export const addBatchTransaction = async (
    itemId: string,
    batch: AcInventoryBatch,
    user: fetchedUser,
    quantityType: AcInventoryItem["quantityType"],
): Promise<StockMovementResult> => {
    const db = firebase.firestore()
    const ref = db.collection(INVENTORY_ITEMS_COLLECTION).doc(itemId)
    const label = getQuantityLabel(quantityType)

    return db.runTransaction(async (tx) => {
        const snap = await tx.get(ref)
        if (!snap.exists) throw new Error("Ürün bulunamadı.")
        const data = snap.data() as AcInventoryItem
        const newBatches = [...(data.batches ?? []), batch]
        const quantity = sumBatchQuantity(newBatches)
        const update: AcInventoryUpdate = {
            updateType: "ADD",
            updateDate: new Date(),
            updateBy: user,
            updateDescription: `${batch.quantity} ${label} eklendi (yeni parti)`,
        }
        tx.update(ref, {
            batches: newBatches,
            quantity,
            updatedAt: new Date(),
            updatedBy: user,
            updates: firebase.firestore.FieldValue.arrayUnion(update),
        })
        return { batches: newBatches, quantity, update }
    })
}

// Thrown when a removal exceeds the (freshly read) available quantity.
export class InsufficientStockError extends Error {
    available: number
    constructor(available: number, label: string) {
        super(`Mevcut stoktan (${available} ${label}) daha fazlasını çıkaramazsınız.`)
        this.name = "InsufficientStockError"
        this.available = available
    }
}

// Remove `amount` from stock (FIFO) atomically, optionally tagging a destination worksite.
// Availability is validated against the fresh quantity inside the transaction, so the
// recorded audit description always matches what was actually consumed.
export const removeStockTransaction = async (
    itemId: string,
    amount: number,
    user: fetchedUser,
    quantityType: AcInventoryItem["quantityType"],
    worksite?: AcInventoryWorksite | null,
): Promise<StockMovementResult> => {
    const db = firebase.firestore()
    const ref = db.collection(INVENTORY_ITEMS_COLLECTION).doc(itemId)
    const label = getQuantityLabel(quantityType)

    return db.runTransaction(async (tx) => {
        const snap = await tx.get(ref)
        if (!snap.exists) throw new Error("Ürün bulunamadı.")
        const data = snap.data() as AcInventoryItem
        const freshBatches = data.batches ?? []
        const available = sumBatchQuantity(freshBatches)
        if (amount > available) throw new InsufficientStockError(available, label)

        const newBatches = consumeBatchesFIFO(freshBatches, amount)
        const quantity = sumBatchQuantity(newBatches)
        const destination = worksite ? ` → ${worksite.name}` : ""
        const update: AcInventoryUpdate = {
            updateType: "REMOVE",
            updateDate: new Date(),
            updateBy: user,
            updateDescription: `${amount} ${label} çıkarıldı (FIFO)${destination}`,
        }
        if (worksite) update.worksite = worksite
        tx.update(ref, {
            batches: newBatches,
            quantity,
            updatedAt: new Date(),
            updatedBy: user,
            updates: firebase.firestore.FieldValue.arrayUnion(update),
        })
        return { batches: newBatches, quantity, update }
    })
}

// ---- Meta propagation ----
// Items embed a snapshot copy of their brand/supplier/category/location. When a meta is
// renamed/recoloured, refresh that embedded copy on every item referencing it so the list
// and detail views don't show stale names. (Items referencing a meta by free-text string,
// e.g. an ad-hoc supplier, are left untouched.)
export const propagateMetaToItems = async (
    field: "productBrand" | "supplier" | "productCategory" | "location",
    meta: { id?: string } & Record<string, any>,
): Promise<void> => {
    if (!meta.id) return
    const db = firebase.firestore()
    const snap = await db.collection(INVENTORY_ITEMS_COLLECTION).where(`${field}.id`, "==", meta.id).get()
    if (snap.empty) return
    const writeBatch = db.batch()
    snap.forEach((doc) => writeBatch.update(doc.ref, { [field]: meta }))
    await writeBatch.commit()
}
